/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { ACTION, ACTION_SUBJECT, ACTION_SUBJECT_ID, EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { cardMessages as messages } from '@atlaskit/editor-common/messages';
import { FloatingToolbarButton as Button, FloatingToolbarSeparator as Separator, SmallerEditIcon } from '@atlaskit/editor-common/ui';
import { getDatasourceType } from '@atlaskit/editor-common/utils';
import { Flex } from '@atlaskit/primitives';
import { showDatasourceModal } from '../pm-plugins/actions';
import { focusEditorView, isDatasourceConfigEditable } from '../utils';
import { CardContextProvider } from './CardContextProvider';
import { useFetchDatasourceInfo } from './useFetchDatasourceInfo';
var buttonStyles = css({
  pointerEvents: 'auto'
});

// Edit button in toolbar to open datasource modal. This button is shown for inline, block, and embed cards
// if they can resolve into a datasource.
var EditDatasourceButtonWithCardContext = function EditDatasourceButtonWithCardContext(_ref) {
  var cardContext = _ref.cardContext,
    datasourceIdFromAdf = _ref.datasourceId,
    intl = _ref.intl,
    editorAnalyticsApi = _ref.editorAnalyticsApi,
    url = _ref.url,
    editorView = _ref.editorView,
    currentAppearance = _ref.currentAppearance;
  var _useFetchDatasourceIn = useFetchDatasourceInfo({
      isRegularCardNode: true,
      url: url,
      cardContext: cardContext
    }),
    datasourceIdFromUrl = _useFetchDatasourceIn.datasourceId,
    extensionKey = _useFetchDatasourceIn.extensionKey;
  var datasourceId = datasourceIdFromUrl !== null && datasourceIdFromUrl !== void 0 ? datasourceIdFromUrl : datasourceIdFromAdf;
  var onEditDatasource = useCallback(function () {
    if (editorView && datasourceId) {
      editDatasource(datasourceId, editorAnalyticsApi, currentAppearance, extensionKey)(editorView.state, editorView.dispatch);
      focusEditorView(editorView);
    }
  }, [currentAppearance, datasourceId, editorAnalyticsApi, editorView, extensionKey]);
  if (!datasourceId || !isDatasourceConfigEditable(datasourceId)) {
    return null;
  }
  if (url) {
    var _cardContext$store, _urlState$error;
    var urlState = cardContext === null || cardContext === void 0 || (_cardContext$store = cardContext.store) === null || _cardContext$store === void 0 ? void 0 : _cardContext$store.getState()[url];
    if ((urlState === null || urlState === void 0 || (_urlState$error = urlState.error) === null || _urlState$error === void 0 ? void 0 : _urlState$error.kind) === 'fatal') {
      return null;
    }
  }
  return jsx(Flex, null, jsx(Button, {
    css: buttonStyles,
    title: intl.formatMessage(messages.datasourceTitle),
    icon: jsx(SmallerEditIcon, null),
    selected: false,
    onClick: onEditDatasource,
    testId: 'card-edit-datasource-button'
  }), jsx(Separator, null));
};
export var EditDatasourceButton = function EditDatasourceButton(_ref2) {
  var datasourceId = _ref2.datasourceId,
    intl = _ref2.intl,
    editorAnalyticsApi = _ref2.editorAnalyticsApi,
    url = _ref2.url,
    editorView = _ref2.editorView,
    currentAppearance = _ref2.currentAppearance;
  return jsx(CardContextProvider, null, function (_ref3) {
    var cardContext = _ref3.cardContext;
    return jsx(EditDatasourceButtonWithCardContext, {
      datasourceId: datasourceId,
      url: url,
      intl: intl,
      editorAnalyticsApi: editorAnalyticsApi,
      editorView: editorView,
      cardContext: cardContext,
      currentAppearance: currentAppearance
    });
  });
};
export var editDatasource = function editDatasource(datasourceId, editorAnalyticsApi, appearance, extensionKey) {
  return function (state, dispatch) {
    var datasourceType = getDatasourceType(datasourceId);
    if (dispatch && datasourceType) {
      var tr = state.tr;
      showDatasourceModal(datasourceType)(tr);
      editorAnalyticsApi === null || editorAnalyticsApi === void 0 || editorAnalyticsApi.attachAnalyticsEvent({
        action: ACTION.CLICKED,
        actionSubject: ACTION_SUBJECT.BUTTON,
        actionSubjectId: ACTION_SUBJECT_ID.EDIT_DATASOURCE,
        eventType: EVENT_TYPE.UI,
        attributes: {
          extensionKey: extensionKey,
          appearance: appearance
        }
      })(tr);
      dispatch(tr);
      return true;
    }
    return false;
  };
};